import type { Direction, PersonDetail, SubmitPresenceResponse } from '@sentra/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { newIdempotencyKey } from '@/api/client';
import { hasCode } from '@/api/errors';
import { applyWriteEffects } from '@/api/invalidation';
import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { useApiClient } from '@/features/auth/session';
import { DirectionButton } from '@/features/direction-button';
import type { AnomalyDecision, AnomalyKind } from '@/features/scan/anomaly';
import { AnomalyDialog, browserAudio, createAlarm } from '@/features/scan/anomaly';

/**
 * A movement recorded by SEARCH rather than by scanning a band (AD-53).
 *
 * **It lives on the record screen, and that is where it has to live.** The gate's panel is rendered
 * from a scan's answer, which a person id cannot produce; the only route that answers by id is
 * `person.get`, whose reading is the audited full disclosure this screen has already performed. A
 * menu entry that navigated to the gate with a person id it could not use was the "the field
 * validates and nothing happens" defect in navigation form.
 *
 * **The movement carries no credential and says how it was reached.** `credentialId: null` is
 * legitimate and common — a manual search, a person who has lost their band — and
 * `method: 'manual_search'` is what puts it on the manual side of Section 1.12's ratio.
 *
 * **A contradiction is asked about FIRST; a refusal is submitted first.** That asymmetry is Section
 * 1.11's own and it is easy to get backwards: a contradicting direction with no reason writes NO
 * row, so asking first keeps a refusal nobody made out of the anomalies panel — while a blocked
 * person's refusal IS a fact about the world, so the submission goes first and the alert dialog
 * opens from the answer.
 *
 * **The idempotency key belongs to the ATTEMPT, not to the press** (AD-30). A rejection does not
 * consume it, so the reasoned retry lands on the same key; minting one per press would make that
 * retry a second movement.
 *
 * **The pair is the gate's own control** (`features/direction-button.tsx`), so an entry is green
 * and an exit is red here exactly as it is at the door, and the expected one is the solid, wider
 * one. It used to be drawn here separately, with no icons and no `data-direction`, so the same
 * act looked like two different acts depending on which screen an operator reached it from.
 *
 * **A refusal is a BANNER here rather than loose red text, and that is not decoration.** In a
 * residence almost everybody is recorded inside, so the expected direction is `exit` and the
 * filled RED button is the ordinary state of this screen — and a red sentence a few pixels under
 * it would be the same colour saying a different thing. The border and the box make the refusal a
 * different OBJECT, which is what `features/scan/anomaly.tsx` already does with the same message.
 */
export function ManualMovement({ person }: Readonly<{ person: PersonDetail }>): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const queries = useQueryClient();
  const describe = useApiErrorMessage();

  const [alarm] = useState(() => createAlarm(browserAudio));
  const [submitting, setSubmitting] = useState(false);
  const [recorded, setRecorded] = useState<SubmitPresenceResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    readonly direction: Direction;
    readonly kind: AnomalyKind;
    readonly key: string;
    readonly failure: string | null;
  } | null>(null);

  /** The direction the recorded state expects. The other one is a contradiction by definition. */
  const expected: Direction = person.presenceState === 'inside' ? 'exit' : 'entry';
  const contradicting: Direction = expected === 'entry' ? 'exit' : 'entry';

  const submit = useCallback(
    (direction: Direction, key: string, decision: AnomalyDecision | null) => {
      setSubmitting(true);
      setFailure(null);
      void (async () => {
        try {
          const answer = await client.request(apiRoutes.presenceSubmit, {
            body: {
              personId: person.id,
              credentialId: null,
              direction,
              method: 'manual_search',
              ...(decision === null
                ? {}
                : {
                    reasonCode: decision.reasonCode,
                    ...(decision.reasonNote === null ? {} : { reasonNote: decision.reasonNote }),
                  }),
            },
            idempotencyKey: key,
          });
          setSubmitting(false);
          // The same movement the gate records, from the record screen. What it changes is declared
          // beside the route; this call site exists because the submission goes through the client
          // directly, to carry AD-30's key.
          applyWriteEffects(queries, apiRoutes.presenceSubmit, {
            params: {},
            body: { personId: person.id },
            result: answer,
          });

          if (answer.refusal?.code === 'PERSON_BLOCKED') {
            // A 200, not an error: the refusal WAS recorded, and what is needed now is a reason.
            // The retry lands on the same key, because a rejection did not consume it.
            setPending({ direction, kind: 'blocked', key, failure: null });
            return;
          }
          setPending(null);
          setRecorded(answer);
        } catch (error) {
          setSubmitting(false);
          if (hasCode(error, 'PRESENCE_REASON_REQUIRED')) {
            // Nothing was written: the state moved under us, or the operator chose the direction
            // the record contradicts. The question the server asked is the one the dialog asks.
            setPending({ direction, kind: 'contradiction', key, failure: null });
            return;
          }
          const message = describe(error);
          setPending((open) => (open === null ? null : { ...open, failure: message }));
          setFailure(message);
        }
      })();
    },
    [client, describe, person.id, queries],
  );

  function begin(direction: Direction): void {
    const key = newIdempotencyKey();
    alarm.prime();
    if (direction !== expected) {
      // Asked BEFORE anything is sent: a contradicting direction with no reason writes no row at
      // all, so submitting first would put a refusal nobody made into the anomalies panel.
      setPending({ direction, kind: 'contradiction', key, failure: null });
      return;
    }
    submit(direction, key, null);
  }

  return (
    <section className="flex flex-col gap-2" data-testid="manual-movement">
      <h3 className="text-sm font-semibold">{t('person.movement.title')}</h3>
      <p className="text-sm text-muted-foreground">{t('person.movement.body')}</p>

      <div className="flex flex-wrap gap-2">
        <DirectionButton
          direction={expected}
          expected
          disabled={submitting}
          data-testid="movement-expected"
          label={t(expected === 'entry' ? 'person.movement.entry' : 'person.movement.exit')}
          onClick={() => {
            begin(expected);
          }}
        />
        <DirectionButton
          direction={contradicting}
          expected={false}
          disabled={submitting}
          data-testid="movement-contradicting"
          label={t(contradicting === 'entry' ? 'person.movement.entry' : 'person.movement.exit')}
          onClick={() => {
            begin(contradicting);
          }}
        />
      </div>

      {recorded !== null && (
        <Alert data-testid="movement-recorded">
          <AlertTitle>
            {t(
              recorded.refusal === null
                ? 'person.movement.recordedTitle'
                : 'person.movement.refusedTitle',
            )}
          </AlertTitle>
          <AlertDescription>
            {t('person.movement.recordedBody', {
              state: t(`person.table.presence_${recorded.presenceState}`),
            })}
          </AlertDescription>
        </Alert>
      )}

      {failure !== null && pending === null && (
        <p className="rounded-md border border-danger p-3 text-sm text-danger" role="alert">
          {failure}
        </p>
      )}

      {/*
        **Mounted only while there IS an anomaly, never toggled**, which is the contract
        `AnomalyDialog` states in its own words and which `features/scan/gate.tsx` honours. Its
        reason, note, acknowledgment and mute are its own `useState` and they sit OUTSIDE the Radix
        `Dialog`, so closing unmounts only the content and every one of those values survives.
        Toggled — as this call site did — a second movement on the same record opened with the
        reason already chosen and the acknowledgment already ticked, so one click wrote a permanent
        `override` row under a reason nobody chose for THAT movement, into a table that admits no
        DELETE; and a mute survived, so every later anomaly on the record was silent, which is
        AD-31's whole point defeated.
      */}
      {pending !== null && (
        <AnomalyDialog
          open
          kind={pending.kind}
          presenceState={person.presenceState}
          alarm={alarm}
          submitting={submitting}
          failure={pending.failure}
          onConfirm={(decision) => {
            submit(pending.direction, pending.key, decision);
          }}
          onCancel={() => {
            // Nothing at all is sent: a declined anomaly must leave no trace, which is the whole
            // reason the contradicting case is asked about before the request rather than after.
            setPending(null);
          }}
        />
      )}
    </section>
  );
}
